import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { ProfileHandling } from "services";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

type PreApplication = Schema["PreApplication"]["type"];

class PreApplicationService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userData = await ProfileHandling.getUserDataFromToken(idToken);
    const userDataWithId = { ...userData };

    const userId = session.tokens?.idToken?.payload?.sub as string;
    const isAuthenticated = !!token;

    return {
      idToken,
      token,
      userId,
      isAuthenticated,
      userData: userDataWithId,
    };
  }

  async submitPreApplication(input: any): Promise<any> {
    try {
      const { token, userData } = await this.getAuthInfo();

      const data = await client.mutations.submitPreApplication(
        {
          input: {
            ...input,
            owner: userData.id,
          },
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return data;
    } catch (error) {
      console.error("Error in genuine student process:", error);
      throw error;
    }
  }

  async getPreApplications(): Promise<any[]> {
    const { userData, token } = await this.getAuthInfo();

    const { data, errors } = await client.models.PreApplication.list({
      authMode: "userPool",
      authToken: token,
    });

    if (errors) {
      console.error(`Error fetching preapplications:`, errors);
      throw new Error("Failed to fetch preapplications");
    }

    const applications = data.filter((app) => app.userId === userData.id) || [];

    if (!applications) {
      throw new Error("Applications not found");
    }

    return applications;
  }

  async getPreApplicationDetails(id: string): Promise<PreApplication | null> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.PreApplication.get(
        {
          preApplicationId: id,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error(
          `Error fetching preApplication details for ID ${id}:`,
          errors,
        );
        throw new Error("Failed to fetch preApplication details");
      }

      return data;
    } catch (error) {
      console.error(
        `Error fetching preApplication details for ID ${id}:`,
        error,
      );
      throw error;
    }
  }

  async deletePreApplication(preApplicationId: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.PreApplication.delete(
        { preApplicationId },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error deleting preApplication.");
        throw new Error("Failed to delete preApplication");
      }

      return data;
    } catch (error) {
      console.error("Error deleting preApplication:", error);
      throw error;
    }
  }

  extractPreApplicationId(response: any): string {
    try {
      const responseBody = response.data || response;

      if (!responseBody) {
        throw new Error("Failed to find data in response.");
      }

      const idRegex = /preApplicationId=([a-f0-9-]+)/;
      const idMatch = responseBody.match(idRegex);

      if (!idMatch || !idMatch[1]) {
        throw new Error("Failed to extract ID from response.");
      }

      return idMatch[1];
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error("Failed to extract preApplication ID: " + errorMessage);
    }
  }

  async updateLinkedApplication(
    preApplicationId: string,
    linkedApplicationId: string,
    institutionId: string,
  ): Promise<PreApplication | null> {
    try {
      const { token } = await this.getAuthInfo();
      const { data, errors } = await client.models.PreApplication.update(
        {
          preApplicationId,
          linkedApplicationId,
          institutionIds: [institutionId],
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error updating linked application:", errors);
        throw new Error("Failed to update linked application");
      }

      return data;
    } catch (error) {
      console.error("Error updating linked application:", error);
      throw error;
    }
  }
}

export const preApplicationService = new PreApplicationService();
